import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Car, Inbox, Images, FolderOpen, Settings, ExternalLink, LogOut, Loader2, Menu, X, BadgePercent } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/vehicles", label: "Vehicles", icon: Car, testid: "admin-nav-vehicles" },
  { to: "/admin/offers", label: "Offers", icon: BadgePercent, testid: "admin-nav-offers" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "admin-nav-leads" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "admin-nav-gallery" },
  { to: "/admin/media", label: "Media Library", icon: FolderOpen, testid: "admin-nav-media" },
  { to: "/admin/settings", label: "Site Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => { document.title = "Admin — West Canal Motors"; }, []);

  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-obsidian"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div>;
  if (user === null) return <Navigate to="/admin/login" replace />;

  if (user.role !== "admin") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-obsidian px-6 text-center text-white" data-testid="admin-not-authorized">
        <p className="eyebrow">Access denied</p>
        <h1 className="mt-4 font-display text-3xl font-bold">This account isn't authorized.</h1>
        <p className="mt-3 max-w-md text-slate-400">You're signed in as <span className="text-white">{user.email}</span>. Ask the dealership owner to add this email to the admin list, or sign in with a different account.</p>
        <button onClick={logout} data-testid="admin-not-authorized-logout" className="btn-ghost mt-8">Sign out</button>
      </div>
    );
  }

  const sidebar = (
    <>
      <div className="px-6 py-6"><Logo subtitle="Admin" /></div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={({ isActive }) => cn("flex items-center gap-3 rounded-md px-3 py-2.5 font-display text-sm font-medium transition-colors", isActive ? "bg-crimson/15 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
            {({ isActive }) => (<><n.icon className={cn("h-4 w-4", isActive && "text-crimson")} />{n.label}</>)}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <Link to="/" target="_blank" data-testid="admin-view-site-link" className="flex items-center gap-3 rounded-md px-3 py-2.5 font-display text-sm text-slate-400 hover:bg-white/5 hover:text-white"><ExternalLink className="h-4 w-4" /> View website</Link>
        <div className="mt-3 flex items-center gap-3 px-3">
          {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-crimson font-display text-xs font-bold">{(user.name || user.email)[0].toUpperCase()}</span>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" data-testid="admin-user-name">{user.name || "Admin"}</p>
            <p className="truncate text-xs text-slate-500">{user.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" title="Sign out" className="text-slate-400 hover:text-crimson"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-white/10 bg-surface/60 lg:flex">{sidebar}</aside>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 lg:hidden">
        <Logo subtitle="Admin" />
        <button onClick={() => setOpen((v) => !v)} className="flex h-10 w-10 items-center justify-center rounded-md border border-white/15" data-testid="admin-mobile-menu-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
      </div>
      {open && <aside className="fixed inset-0 z-40 flex flex-col bg-obsidian pt-16 lg:hidden">{sidebar}</aside>}
      <main className="px-4 py-8 sm:px-8 lg:ml-64 lg:px-12 lg:py-12">
        <Outlet />
      </main>
    </div>
  );
}
